import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { REGEX_IS_EMAIL, SITE_URLS } from '@constants';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@hooks/useNotifications';
import { useForgotPassword } from '@services/useForgotPassword';

interface RecoverPasswordErrors {
    email: string[];
}

interface RecoverPasswordFormProps {
    email?: string;
    onSuccess: (email: string) => void;
}

export const RecoverPasswordForm: React.FC<RecoverPasswordFormProps> = ({ email: initialEmail = '', onSuccess }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showDefaultNotification } = useNotifications();

    const { request, success, error, loading, lastResponseTime } = useForgotPassword();

    const formRef = useRef<HTMLFormElement>(null);
    const [email, setEmail] = useState(initialEmail);
    const [errors, setErrors] = useState<RecoverPasswordErrors>({ email: [] });

    const translations = {
        title: t('recover.title'),
        description: t('recover.description'),
        emailRequired: t('login.email.required-error'),
        emailInvalid: t('login.email.invalid-error'),
        submit: t('recover.submit'),
        backToLogin: t('recover.back'),
        success: t('recover.success'),
    };

    const validate = (): boolean => {
        const newErrors: RecoverPasswordErrors = { email: [] };

        if (!email.trim()) {
            newErrors.email.push(translations.emailRequired);
        } else if (!REGEX_IS_EMAIL.test(email.trim())) {
            newErrors.email.push(translations.emailInvalid);
        }

        setErrors(newErrors);
        return newErrors.email.length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        request({ email });
    };

    useEffect(() => {
        if (!lastResponseTime) return;

        if (success) {
            showDefaultNotification({ type: 'success', message: translations.success });
            onSuccess(email);
        }

        if (error) {
            showDefaultNotification({
                type: 'error',
                message: error,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form formRef={formRef} classes="d-flex flex-column w-100" onSubmit={() => {}}>
            <Typography fontSize="20" fontWeight="700" align="center">
                {translations.title}
            </Typography>

            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

            <Typography fontSize="14" align="center">
                {translations.description}
            </Typography>

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <Input
                type={InputTypes.email}
                label="Email*"
                variant={InputVariants.labeledInput}
                value={email}
                onChange={setEmail}
                state={errors.email.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.email}
                data-testid="recover-email-input"
            />

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <Button label={translations.submit} mode={ButtonMode.primary} onClick={handleSubmit} disabled={loading} />

            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

            <Button label={translations.backToLogin} mode={ButtonMode.primary} onClick={() => navigate(`/${SITE_URLS.login}`)} />
        </Form>
    );
};
